<template>
    <div class="sl-card">
        <!-- Title -->
        <div class="sl-card__title">
            {{ t('card.order_journal.title') }}
        </div>

        <!-- Card Contents While Loading -->
        <div v-if="loading" class="flex items-center justify-center">
            <span class="loader"></span>
        </div>

        <!-- Card Content -->
        <template v-else>
            <!-- Order Placed by -->
            <div v-if="order.extra?.placed_by_mean" class="sl-card-details__line">
                <div class="sl-card-details__line-label">
                    {{ t('order_table.details.section.complements.placed_by_mean.label') }}:
                </div>

                <div class="sl-card-details__line-value">
                    {{ pf(order.extra.placed_by_mean, 'placed_by_mean', 'value') }}
                </div>
            </div>

            <!-- User: Added by -->
            <div class="sl-card-details__line">
                <div class="sl-card-details__line-label">
                    {{ t('order_table.details.section.users.added_by.label') }}:
                </div>

                <div class="sl-card-details__line-value">
                    {{ users.addedBy }}
                </div>
            </div>

            <!-- User: Placed by -->
            <div class="sl-card-details__line">
                <div class="sl-card-details__line-label">
                    {{ t('order_table.details.section.users.placed_by.label') }}:
                </div>

                <div class="sl-card-details__line-value">
                    {{ users.placedBy }}
                </div>
            </div>

            <!-- User: Reviewed by -->
            <div v-if="reviewed" class="sl-card-details__line">
                <div class="sl-card-details__line-label">
                    {{ t('order_table.details.section.users.reviewed_by.label') }}:
                </div>

                <div class="sl-card-details__line-value">
                    {{ users.reviewedBy }}
                </div>
            </div>

            <!-- Order Status Table -->
            <table class="sl-card-table mt-4">
                <tbody>
                    <tr v-for="status in order.status" :key="status.code">
                        <!-- Status Code -->
                        <td class="sl-card-table-cell sl-card-table-cell__label">
                            {{ status.code }}
                        </td>

                        <!-- Status Timestamp -->
                        <td class="sl-card-table-cell sl-card-table-cell__value text-right">
                            {{ formatDate(status.at) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import axios from '@/axios';
import { pf } from '@/utils/vue-formatter';

// ------------------------------------------------------------ PROPS

const props = defineProps({
    order:
    {
        type:     Object,
        required: true,
    },
});

// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });

// ------------------------------------------------------------ DATA

const loading = ref(true);
const users = ref({
    addedBy:    null,
    placedBy:   null,
    reviewedBy: null,
});

// ------------------------------------------------------------ COMPUTED

/**
 * An order is considered *reviewed* when it has had the `WAITING_FOR_REVIEW`
 * status, but it's not the latest one.
 */
const reviewed = computed(() =>
{
    const statuses = props.order.status;
    const pos = statuses.findIndex(s => s.code === 'WAITING_FOR_REVIEW');
    return pos !== -1 && pos < (statuses.length - 1);
});

// ------------------------------------------------------------ METHODS

async function fetchUsers()
{
    loading.value = true;
    const notFound = t('common.errors.http.not_found');

    try
    {
        const { data } = await axios.get(`/api/order/reference/${props.order.reference}/users`);
        users.value = {
            addedBy:    data.added_by?.username ?? notFound,
            placedBy:   data.placed_by?.username ?? notFound,
            reviewedBy: data.reviewed_by?.username ?? notFound,
        };
    }
    catch(error)
    {
        if(error.response?.status === 404)
        {
            users.value = {
                addedBy:    notFound,
                placedBy:   notFound,
                reviewedBy: notFound,
            };
            return;
        }
    }
    finally
    {
        loading.value = false;
    }
}

function formatDate(date)
{
    return moment(date).format('DD.MM.YY | HH:mm');
}

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(async() =>
{
    await fetchUsers();
});
</script>

<style scoped>
@reference "#main.css";

@media (width < 96rem)
{
    .sl-card-table-cell__label
    {
        @apply max-w-[150px] truncate;
    }
}
</style>
